import React from 'react'

import { IUI2FormContext, UI2FormContext } from './ui2-form-context'

/**
 * True when the uiSchema node for this component set `isHorizontalLayout`
 * itself, as opposed to inheriting it.
 *
 * Testing `props.isHorizontalLayout !== undefined` does NOT answer this: the
 * abstract renderer spreads its top-level context object (which usually
 * carries `isHorizontalLayout`) into every component's props, so the prop is
 * essentially never undefined. The uiSchema node is the only place the
 * author's intent survives.
 *
 * Used by the HOC below to decide whether a field's own layout default
 * applies, and directly by fields that can't hang a `defaultProps` static off
 * themselves (function components — React 18.3 deprecates that).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function schemaSetsHorizontalLayout(props: any): boolean {
  return props?.frames?.getContext?.('uiSchema')?.isHorizontalLayout !== undefined
}

/**
 * Injects UI2FormContext values as props on a UI2 field/section.
 *
 * Precedence rules:
 *  - `formDensity`, `isRequired`, `isOptional` — child prop wins over context
 *    (context supplies a form-level default the child can override).
 *  - `isReadOnly`, `isDisabled` — context wins when the form sets it to
 *    `true`. A form-enforced read-only/disabled state cannot be overridden
 *    by a child prop. When the form leaves either undefined (or sets it to
 *    `false`), the child prop takes effect.
 *
 * Forwards refs so the entity renderer can track the wrapped class instance
 * for UI-driven validation.
 */
export function withUI2FormContext<P extends Partial<IUI2FormContext>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  WrappedComponent: any
): React.ComponentType<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

  const WithUI2FormContext = React.forwardRef<unknown, P>((props, ref) => (
    <UI2FormContext.Consumer>
      {(context) => {
        const merged: IUI2FormContext & Record<string, unknown> = {
          ...context,
          ...props,
        }
        // Force-on: context wins whenever the form set it to true.
        if (context.isReadOnly === true) {
          merged.isReadOnly = true
        }
        if (context.isDisabled === true) {
          merged.isDisabled = true
        }
        // isHorizontalLayout: when the component has a defaultProps for this prop AND the
        // uiSchema node for this component does NOT explicitly set it (meaning the value
        // came from the renderer context spread, not from a schema author), apply the
        // field's own defaultProps. This lets fields like fileUploadField stay vertical by
        // default inside a horizontally-laid-out form, while still respecting an explicit
        // isHorizontalLayout in the uiSchema.
        //
        // See `schemaSetsHorizontalLayout` above for why the uiSchema node is
        // consulted rather than the prop.
        if (!schemaSetsHorizontalLayout(props) && WrappedComponent.defaultProps?.isHorizontalLayout !== undefined) {
          merged.isHorizontalLayout = WrappedComponent.defaultProps.isHorizontalLayout
        }
        // Force-null: completed stacked sections suppress field-level decorations.
        if (context.inlineHelpText === null) {
          merged.inlineHelpText = null
          merged.isRequired = false
          merged.isOptional = false
        }
        return <WrappedComponent {...merged} ref={ref} />
      }}
    </UI2FormContext.Consumer>
  ))

  WithUI2FormContext.displayName = `WithUI2FormContext(${displayName})`
  // Forward renderer-read statics so the entity renderer's scope-isolation
  // detection still works when a component is wrapped by this HOC.
  if (WrappedComponent.isolatesChildValueScope !== undefined) {
    ;(WithUI2FormContext as any).isolatesChildValueScope = WrappedComponent.isolatesChildValueScope
  }
  return WithUI2FormContext as unknown as React.ComponentType<P>
}
